/** ArticleDocumentEditor — schema-driven form for editing the ArticleDocument. */

import React, { useEffect } from 'react';
import { useArticleContext } from './ArticleContext';
import { Field, ReadOnlyField, SectionHeading, StringListEditor, TextArea, TextInput } from './ArticleDocumentEditorFields';
import { ArticleSectionsEditor, ContentResearchEditor, FaqEditor } from './ArticleDocumentEditorSections';
import { useDocumentDraft } from './useDocumentDraft';

const SECTION_IDS = {
  thesis: 'editor-thesis',
  'targeting.keywords': 'editor-targeting-keywords',
  'targeting.serp': 'editor-targeting-serp',
  'targeting.competitors': 'editor-targeting-competitors',
  'content_research': 'editor-content-research',
  'content_research.internal_link_candidates': 'editor-content-research',
  'article': 'editor-article',
  'article.sections': 'editor-article-sections',
  'article.seo': 'editor-article-seo',
  'article.faq': 'editor-article-faq',
  'article.ctas': 'editor-article-ctas',
  'article.markdown': 'editor-article-markdown',
  'preflight': 'editor-preflight',
  'preflight.ai_queries': 'editor-preflight-queries',
} as const;

export function ArticleDocumentEditor() {
  const {
    document: originalDoc,
    candidate,
    run,
    editorFocusSection,
    refreshDocument,
    refreshArticles,
  } = useArticleContext();

  const {
    draft,
    saving,
    saveError,
    savedAt,
    scrolledRef,
    updateThesis,
    updateSeo,
    updateArticleField,
    updateSection,
    addSection,
    removeSection,
    updateFaq,
    addFaq,
    removeFaq,
    updateLink,
    addLink,
    removeLink,
    updateRef,
    addRef,
    removeRef,
    handleSave,
  } = useDocumentDraft({
    originalDoc,
    runId: run?.run_id,
    candidate,
    refreshDocument,
    refreshArticles,
  });

  // Auto-scroll to focus section
  useEffect(() => {
    if (editorFocusSection && !scrolledRef.current && draft) {
      const elementId = SECTION_IDS[editorFocusSection as keyof typeof SECTION_IDS];
      if (elementId) {
        setTimeout(() => {
          const el = document.getElementById(elementId);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      scrolledRef.current = true;
    }
  }, [editorFocusSection, draft, scrolledRef]);

  if (!draft) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No document to edit. Run at least one stage first.
      </div>
    );
  }

  const thesis = draft.thesis;
  const targeting = draft.targeting;
  const contentResearch = draft.content_research;
  const article = draft.article;
  const preflight = draft.preflight;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-2 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Article Document</h3>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs text-green-600">Saved at {savedAt}</span>}
          {saveError && <span className="text-xs text-red-600">{saveError}</span>}
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#1a2332' }}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Thesis */}
      <SectionHeading id={SECTION_IDS.thesis} title="Thesis">
        <Field label="Article name">
          <TextInput value={thesis.candidate_name} onChange={(v) => updateThesis('candidate_name', v)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ICP">
            <TextInput value={thesis.icp} onChange={(v) => updateThesis('icp', v)} />
          </Field>
          <Field label="Content pillar">
            <TextInput value={thesis.content_pillar || ''} onChange={(v) => updateThesis('content_pillar', v)} />
          </Field>
          <Field label="Funnel stage">
            <select
              className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm"
              value={thesis.funnel_stage}
              onChange={(e) => updateThesis('funnel_stage', e.target.value)}
            >
              <option value="Top">Top</option>
              <option value="Medium">Medium</option>
              <option value="Bottom">Bottom</option>
              <option value="Authority">Authority</option>
            </select>
          </Field>
          <Field label="Focus">
            <select
              className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm"
              value={thesis.focus}
              onChange={(e) => updateThesis('focus', e.target.value)}
            >
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
          </Field>
        </div>
        <Field label="Business context">
          <TextArea value={thesis.business_context || ''} onChange={(v) => updateThesis('business_context', v)} rows={2} />
        </Field>
        <Field label="Directional guidance">
          <TextArea value={thesis.directional_guidance || ''} onChange={(v) => updateThesis('directional_guidance', v)} rows={2} />
        </Field>
        <Field label="Editorial direction">
          <TextArea value={thesis.editorial_direction || ''} onChange={(v) => updateThesis('editorial_direction', v)} rows={2} />
        </Field>
      </SectionHeading>

      {/* Targeting — read-only summary */}
      {targeting && (
        <SectionHeading id={SECTION_IDS['targeting.keywords']} title="Targeting">
          {targeting.keywords?.primary && (
            <ReadOnlyField label="Primary keyword" value={`${targeting.keywords.primary.keyword} (vol: ${targeting.keywords.primary.volume ?? '\u2014'}, kd: ${targeting.keywords.primary.kd ?? '\u2014'})`} />
          )}
          {targeting.keywords?.secondary && targeting.keywords.secondary.length > 0 && (
            <ReadOnlyField label="Secondary keywords" value={targeting.keywords.secondary.map((k) => k.keyword).join(', ')} />
          )}
          {targeting.keywords?.keyword_decision && (
            <ReadOnlyField label="Keyword decision" value={`${targeting.keywords.keyword_decision.decision} \u2014 ${targeting.keywords.keyword_decision.seo_signal}`} />
          )}
          {targeting.serp?.overview && (
            <ReadOnlyField label="SERP decision" value={`${targeting.serp.overview.decision} \u2014 ${targeting.serp.overview.dominant_content_format || ''}`} />
          )}
          {targeting.competitors?.analysis && (
            <ReadOnlyField label="Target word count" value={`${targeting.competitors.analysis.target_word_count || '\u2014'}`} />
          )}
        </SectionHeading>
      )}

      {/* Content Research */}
      <ContentResearchEditor
        sectionId={SECTION_IDS['content_research']}
        contentResearch={contentResearch}
        updateLink={updateLink}
        addLink={addLink}
        removeLink={removeLink}
        updateRef={updateRef}
        addRef={addRef}
        removeRef={removeRef}
      />

      {/* Article — SEO */}
      {article?.seo && (
        <SectionHeading id={SECTION_IDS['article.seo']} title="SEO Metadata">
          <Field label={`Meta title (${(article.seo.meta_title || '').length}/60)`}>
            <TextInput value={article.seo.meta_title} onChange={(v) => updateSeo('meta_title', v)} />
          </Field>
          <Field label={`Meta description (${(article.seo.meta_description || '').length}/155)`}>
            <TextArea value={article.seo.meta_description} onChange={(v) => updateSeo('meta_description', v)} rows={2} />
          </Field>
          <Field label="Slug">
            <TextInput value={article.seo.slug} onChange={(v) => updateSeo('slug', v)} />
          </Field>
        </SectionHeading>
      )}

      {/* Article — Summary fields */}
      {article && (
        <SectionHeading id={SECTION_IDS['article']} title="Article Overview">
          <Field label="Summary">
            <TextArea value={article.summary || ''} onChange={(v) => updateArticleField('summary', v)} rows={2} />
          </Field>
          <Field label="Narrative arc">
            <TextArea value={article.narrative_arc || ''} onChange={(v) => updateArticleField('narrative_arc', v)} rows={2} />
          </Field>
          <Field label="Target reader">
            <TextArea value={article.target_reader || ''} onChange={(v) => updateArticleField('target_reader', v)} rows={2} />
          </Field>
          <Field label="Key takeaways">
            <StringListEditor
              items={article.key_takeaways || []}
              onChange={(v) => updateArticleField('key_takeaways', v)}
              placeholder="Takeaway"
            />
          </Field>
        </SectionHeading>
      )}

      {/* Article — Sections */}
      {article?.sections && article.sections.length > 0 && (
        <ArticleSectionsEditor
          sectionId={SECTION_IDS['article.sections']}
          sections={article.sections}
          updateSection={updateSection}
          addSection={addSection}
          removeSection={removeSection}
        />
      )}

      {/* Article — FAQ */}
      {article?.faq && (
        <FaqEditor
          sectionId={SECTION_IDS['article.faq']}
          faq={article.faq}
          updateFaq={updateFaq}
          addFaq={addFaq}
          removeFaq={removeFaq}
        />
      )}

      {/* Article — Markdown */}
      {article?.markdown != null && (
        <SectionHeading id={SECTION_IDS['article.markdown']} title="Assembled Markdown">
          <TextArea
            value={article.markdown || ''}
            onChange={(v) => updateArticleField('markdown', v)}
            rows={20}
            placeholder="Article markdown..."
          />
        </SectionHeading>
      )}

      {/* Preflight — read-only summaries */}
      {preflight && (
        <SectionHeading id={SECTION_IDS['preflight']} title="Preflight & Production">
          {preflight.validation && (
            <ReadOnlyField label="SEO score" value={`${preflight.validation.overall_score ?? '\u2014'}`} />
          )}
          {preflight.wordpress && (
            <ReadOnlyField label="WordPress" value={`Post #${preflight.wordpress.post_id} \u2014 ${preflight.wordpress.post_status}`} />
          )}
          {Array.isArray(preflight.ai_queries) && preflight.ai_queries.length > 0 && (
            <div id={SECTION_IDS['preflight.ai_queries']}>
              <span className="text-xs text-gray-500">AI Queries ({preflight.ai_queries.length})</span>
              <div className="space-y-1 mt-1">
                {preflight.ai_queries.map((q, i) => (
                  <div key={i} className="text-xs text-gray-600">
                    <span className="font-medium">{q.category}</span>: {q.query}
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionHeading>
      )}
    </div>
  );
}
